/* eslint-disable no-param-reassign */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { OperationInput } from './operation-center.types';
import type {
  AiBlockWithMetadata,
  App,
  FlowCanvasState,
  NewNodeData,
  NewNodeSource,
  NodeMenuState,
  ValidationErrors,
} from './types';
import { IdleMenuState, MenuState } from './types';

const initialState: FlowCanvasState = {
  aiBlocks: [],
  menuState: { type: MenuState.IDLE, state: IdleMenuState.SELECT_NODE },
  isSideMenuOpen: false,
  availableIntegrations: {},
  availableOutputKeys: {},
  errors: {},
  editingNodeId: undefined,
};

export const useFlowCanvas = create<FlowCanvasState, [['zustand/immer', never]]>(
  immer<FlowCanvasState>(() => initialState),
);

export const setNewNodeData = (data?: NewNodeData) =>
  useFlowCanvas.setState((state) => {
    state.newNodeData = { ...state.newNodeData, ...data };
  });

export const setNewNodeSource = (source?: NewNodeSource) =>
  useFlowCanvas.setState((state) => {
    state.newNodeSource = source;
  });

export const toggleSideMenu = () =>
  useFlowCanvas.setState((state) => {
    state.isSideMenuOpen = !state.isSideMenuOpen;
  });

export const setIsSideMenuOpen = (isOpen: boolean) =>
  useFlowCanvas.setState((state) => {
    state.isSideMenuOpen = isOpen;
  });

export const setAiBlocks = (aiBlocks: AiBlockWithMetadata[]) =>
  useFlowCanvas.setState((state) => {
    state.aiBlocks = aiBlocks;
  });

export const reset = () => useFlowCanvas.setState(() => ({ ...initialState }));

export const setSelectedNodeId = (nodeId?: string) =>
  useFlowCanvas.setState((state) => {
    state.selectedNodeId = nodeId;
  });

export const setMenuState = (menuState: NodeMenuState) =>
  useFlowCanvas.setState((state) => {
    state.menuState = menuState;
  });

export const setAvailableIntegrations = (availableIntegrations: Record<string, App>) =>
  useFlowCanvas.setState((state) => {
    state.availableIntegrations = availableIntegrations;
  });

export const setAvailableOutputKeys = (availableOutputKeys: Record<string, Record<string, OperationInput>>) => {
  useFlowCanvas.setState((state) => {
    state.availableOutputKeys = availableOutputKeys;
  });
};

export const setErrors = (errors: ValidationErrors) =>
  useFlowCanvas.setState((state) => {
    state.errors = errors;
  });

// #region RENAMING NODE FEATURE
export const setEditingNode = ({ nodeId, name, icon }: { nodeId?: string; name?: string; icon?: string }) =>
  useFlowCanvas.setState((state) => {
    state.editingNodeId = nodeId;
    state.draftName = name;
    state.draftIcon = icon;
  });

export const resetRenameStatus = () =>
  useFlowCanvas.setState((state) => {
    state.editingNodeId = undefined;
    state.draftName = undefined;
    state.draftIcon = undefined;
  });
// #endregion
